import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from './firebase';
import { createPaymentOrder } from './api';
import "./PricingPage.css";

export default function PricingPage() {
  const navigate = useNavigate();
  const plans = {
    SINGLE_INTERVIEW: {
      name: '1 Interview Pack',
      price: 199,
      period: 'one time',
      description: 'Perfect for trying us out',
      features: [
        '1 AI-Generated Interview',
        'Real-Time Speech Recognition',
        'Detailed Performance Report',
        'Instant Feedback & Tips',
        'Download Interview Report'
      ],
      color: '#667eea'
    },
    FIVE_INTERVIEWS: {
      name: '5 Interviews Pack',
      price: 899,
      period: 'valid for 90 days',
      description: 'Best for regular practice',
      features: [
        '5 AI Interview Sessions',
        '90 Days Validity',
        'Performance Analytics Dashboard',
        'All Features Included',
        'Priority Support'
      ],
      color: '#764ba2',
      isDefault: true,
      badge: 'Popular'
    },
    TEN_INTERVIEWS: {
      name: '10 Interviews Pack',
      price: 1599,
      period: 'valid for 180 days',
      description: 'Great for thorough preparation',
      features: [
        '10 AI Interview Sessions',
        '180 Days (6 Months) Validity',
        'Advanced Analytics',
        'Progress Tracking',
        'All Features + Premium Support'
      ],
      color: '#48bb78'
    },
    TWENTY_INTERVIEWS: {
      name: '20 Interviews Pack',
      price: 1899,
      period: 'valid for 365 days',
      description: 'Best value for serious learners',
      features: [
        '20 AI Interview Sessions',
        '365 Days (1 Year) Validity',
        'Complete Analytics Suite',
        'Career Progress Reports',
        'Premium Support & Resources'
      ],
      color: '#f093fb',
      badge: 'Best Value'
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pricing-container">
      {/* Animated background */}
      <div className="background-animation">
        <div className="gradient-blob blob-1"></div>
        <div className="gradient-blob blob-2"></div>
        <div className="gradient-blob blob-3"></div>
      </div>

      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-content">
          <button onClick={() => navigate(-1)} className="back-button">← Back</button>
          <div className="logo-section">
            <div className="logo-icon">
              <img src="/Vagmee_Logo.png" alt="Vagmee" className="nav-logo" />
            </div>
            <div className="logo-branding">
              <span className="logo-text">Vagmee</span>
              <span className="logo-tagline">Get Access to Interview Practice</span>
            </div>
          </div>
          <div style={{ width: "120px" }}></div>
        </div>
      </nav>

      {/* Pricing Section */}
      <section className="payment-section">
        <div className="pricing-header" style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#fff' }}>Choose Your Plan</h2>
          <p className="section-subtitle" style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.8)' }}>
            Select the plan that works best for you
          </p>
        </div>

        <div className="plans-grid" style={{ 
          display: 'flex', 
          gap: '2rem', 
          justifyContent: 'center', 
          alignItems: 'stretch',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 2rem'
        }}>
          {Object.entries(plans).map(([key, plan]) => (
            <div
              key={key}
              className={`plan-card ${plan.badge ? 'has-badge' : ''}`}
              onClick={() => navigate('/login')}
              style={{
                flex: '1',
                maxWidth: '350px',
                border: `2px solid ${plan.color}`,
                backgroundColor: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(10px)',
                borderRadius: '1rem',
                padding: '2rem',
                position: 'relative',
                transition: 'transform 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-8px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {plan.badge && (
                <div className="plan-badge" style={{
                  position: 'absolute',
                  top: '-12px',
                  right: '20px',
                  backgroundColor: plan.color,
                  color: '#fff',
                  padding: '0.5rem 1rem',
                  borderRadius: '1rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold'
                }}>
                  {plan.badge}
                </div>
              )}
              
              <div className="plan-header" style={{ marginBottom: '1.5rem' }}>
                <h3 className="plan-name" style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#fff' }}>
                  {plan.name}
                </h3>
                <p className="plan-desc" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>
                  {plan.description}
                </p>
              </div>

              <div className="plan-price" style={{ marginBottom: '2rem', display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                <span className="currency" style={{ fontSize: '1.5rem', color: plan.color }}>₹</span>
                <span className="amount" style={{ fontSize: '3rem', fontWeight: 'bold', color: '#fff' }}>{plan.price}</span>
                <span className="period" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1rem' }}>
                  {plan.period}
                </span>
              </div>

              <ul className="plan-features" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {plan.features.map((feature, idx) => (
                  <li key={idx} style={{ 
                    display: 'flex', 
                    gap: '0.75rem', 
                    alignItems: 'flex-start', 
                    marginBottom: '1rem',
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '0.95rem'
                  }}>
                    <span className="check" style={{ color: plan.color, fontSize: '1.2rem', fontWeight: 'bold' }}>✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>Vagmee</h4>
            <p>Practice smarter. Get hired faster.</p>
          </div>
          <div className="footer-section">
            <h5>Product</h5>
            <ul>
              <li><button onClick={() => { navigate('/#features'); }} className="footer-link-button">Features</button></li>
              <li><button onClick={() => navigate('/pricing')} className="footer-link-button">Pricing</button></li>
              <li><button onClick={() => { navigate('/#how-it-works'); }} className="footer-link-button">How It Works</button></li>
            </ul>
          </div>
          <div className="footer-section">
            <h5>Company</h5>
            <ul>
              <li><button onClick={() => navigate('/about#mission-section')} className="footer-link-button">About</button></li>
              <li><button onClick={() => navigate('/about#contact-section')} className="footer-link-button">Contact</button></li>
              <li><button onClick={() => navigate('/about#policies-section')} className="footer-link-button">Policies</button></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 Vagmee. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}